import { Flex, Box, Text, AspectRatio } from "@chakra-ui/react";
import React from "react";
import Image from "next/image";
import { Container } from "ui/container";
import { QuoteIcon } from "ui/icons";
import { ArrowLink } from "ui/link";

type QuoteProps = {
  quote: string;
  author: string;
  authorImage: string;
  job: string;
  company: string;
  logo?: string;
  href?: string;
  isInSlider?: boolean;
  size?: "sm" | "lg";
};

export function Quote({ quote, author, authorImage, job, company, logo, href, isInSlider, size = "lg" }: QuoteProps) {
  return (
    <Container>
      <Flex
        align="center"
        justify="space-between"
        direction={{ base: "column", md: "row" }}
        gap={24}
        pb={isInSlider ? { base: 8, lg: 10 } : 0} // Space for slider pagination
      >
        <Box flex={1}>
          <QuoteIcon boxSize={{ base: 8, md: 10 }} />

          <Text textStyle={{ base: "h7", lg: size === "sm" ? "h7" : "e3" }} mt={4} maxW="3xl">
            {quote}
          </Text>

          <Flex display={{ base: "flex", md: "none" }} align="stretch" gap={5} mt={7} maxW="320px">
            <AspectRatio
              pos="relative"
              ratio={1 / 1}
              w="50%"
              bg="quoteAuthorGradient"
              borderRadius="md"
              overflow="hidden"
            >
              <Image
                src={`${authorImage}?w=500`}
                alt={`${author}.`}
                sizes="100%"
                width={300}
                height={300}
                style={{ width: "100%", height: "100%", maxHeight: "100%", objectFit: "cover" }}
              />
            </AspectRatio>

            {logo && (
              <Flex
                bg="white"
                border="1px solid"
                borderColor="base.border"
                borderRadius="md"
                align="center"
                justify="center"
                w="50%"
                p={4}
              >
                <Image
                  src={`${logo}?w=400`}
                  alt={company}
                  sizes="100%"
                  width={200}
                  height={200}
                  style={{ objectFit: "contain", width: "100%", height: "auto" }}
                />
              </Flex>
            )}
          </Flex>

          <Text flex={1} fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" mt={8}>
            {author}
            <br />
            <Text
              as="span"
              color="text.secondary"
              fontSize={{ base: "sm", md: "md" }}
              fontWeight="normal"
            >{`${job} at ${company}`}</Text>
          </Text>

          {href && (
            <ArrowLink href={href} variant="grass" size="lg" mt={{ base: 5, md: 7 }}>
              See story
            </ArrowLink>
          )}
        </Box>

        <Box
          display={{ base: "none", md: "flex" }}
          pos="relative"
          flex={0.5}
          maxW={{ base: "240px", lg: "280px" }}
          mb={12} // Align with logo offset
        >
          <AspectRatio
            pos="relative"
            ratio={1 / 1}
            w="100%"
            bg="quoteAuthorGradient"
            borderRadius="md"
            overflow="hidden"
          >
            <Image
              src={`${authorImage}?w=600`}
              alt={`${author}.`}
              sizes="100%"
              width={240}
              height={240}
              style={{ width: "100%", height: "100%", maxHeight: "100%", objectFit: "cover" }}
            />
          </AspectRatio>

          {logo && (
            <Flex
              pos="absolute"
              bottom={-12}
              left={-12}
              bg="white"
              boxSize={{ base: "100px", lg: "140px" }}
              border="1px solid"
              borderColor="base.border"
              borderRadius="md"
              align="center"
              justify="center"
              p={{ base: 1, lg: 4 }}
              zIndex={1}
            >
              <Box pos="relative">
                <Image
                  src={`${logo}?w=280`}
                  alt={company}
                  sizes="100%"
                  width={140}
                  height={140}
                  style={{ objectFit: "contain", width: "100%", height: "auto" }}
                />
              </Box>
            </Flex>
          )}
        </Box>
      </Flex>
    </Container>
  );
}
